import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building2, 
  TrendingUp, 
  Shield, 
  CreditCard,
  CheckCircle,
  ArrowRight,
  Users,
  Zap,
  Globe,
  Award,
  Calculator,
  Target,
  Briefcase,
  Factory,
  Truck,
  Store,
  PieChart,
  DollarSign,
  Clock,
  Phone,
  Star,
  Handshake,
  BarChart3,
  Settings,
  Landmark,
  FileText,
  Lightbulb
} from 'lucide-react';

const BusinessBanking = () => {
  const businessSolutions = [
    {
      icon: Building2,
      title: "Business Checking",
      description: "Professional banking accounts designed for businesses of all sizes",
      startingBalance: "$100",
      monthlyFee: "$0*",
      features: [
        "No minimum balance requirement",
        "Unlimited transactions",
        "Online banking & bill pay",
        "Mobile check deposit",
        "Business debit card",
        "Overdraft protection",
        "Free checks",
        "24/7 account access"
      ],
      bgColor: "from-blue-500 to-blue-600",
      popular: true
    },
    {
      icon: DollarSign,
      title: "Business Savings",
      description: "Earn competitive interest while maintaining liquidity",
      startingBalance: "$500",
      monthlyFee: "$0",
      features: [
        "Competitive interest rates",
        "No monthly maintenance fees",
        "FDIC insured up to $250,000",
        "Online banking access",
        "Automatic transfer options",
        "Quarterly statements",
        "Mobile banking",
        "Business relationship perks"
      ],
      bgColor: "from-green-500 to-green-600",
      popular: false
    },
    {
      icon: PieChart,
      title: "Money Market",
      description: "Higher yields with check writing privileges",
      startingBalance: "$10,000",
      monthlyFee: "$15*",
      features: [
        "Tiered interest rates up to 4.25% APY",
        "Check writing privileges",
        "Business debit card",
        "Limited monthly transactions",
        "Higher balance requirements",
        "Premium customer service",
        "Business banking advisor",
        "Priority phone support"
      ],
      bgColor: "from-purple-500 to-purple-600",
      popular: false
    },
    {
      icon: Target,
      title: "Business CDs",
      description: "Secure, guaranteed returns for business funds",
      startingBalance: "$5,000",
      monthlyFee: "$0",
      features: [
        "Guaranteed interest rates",
        "Terms from 3 months to 5 years",
        "Automatic renewal options",
        "FDIC insured",
        "Flexible penalty structures",
        "Business tax reporting",
        "Laddering strategies available",
        "Competitive market rates"
      ],
      bgColor: "from-orange-500 to-orange-600",
      popular: false
    }
  ];

  const lendingSolutions = [
    {
      icon: Factory,
      title: "Equipment Financing",
      description: "Finance machinery, vehicles, and technology",
      rates: "Starting at 5.99% APR",
      maxAmount: "Up to $5M",
      terms: "12-84 months",
      features: [
        "New and used equipment",
        "100% financing available",
        "Quick approval process",
        "Flexible payment terms",
        "Tax advantages",
        "Seasonal payment options"
      ]
    },
    {
      icon: Building2,
      title: "Commercial Real Estate",
      description: "Purchase, refinance, or expand your property",
      rates: "Starting at 6.25% APR",
      maxAmount: "Up to $10M",
      terms: "5-25 years",
      features: [
        "Owner-occupied properties",
        "Investment properties",
        "Construction loans",
        "SBA 504 programs",
        "Flexible down payments",
        "Interest-only options"
      ]
    },
    {
      icon: CreditCard,
      title: "Business Lines of Credit",
      description: "Flexible access to working capital",
      rates: "Starting at 7.25% APR",
      maxAmount: "Up to $2M",
      terms: "Revolving credit",
      features: [
        "Draw funds as needed",
        "Pay interest only on used amount",
        "No collateral required (up to $250k)",
        "Online access and management",
        "Automatic renewal options",
        "Competitive rates"
      ]
    },
    {
      icon: TrendingUp,
      title: "SBA Loans",
      description: "Government-backed financing programs",
      rates: "Starting at 11.5% APR",
      maxAmount: "Up to $5M",
      terms: "Up to 25 years",
      features: [
        "SBA 7(a) loans",
        "SBA Express loans",
        "Lower down payments",
        "Longer repayment terms",
        "Startup funding available",
        "Expert SBA guidance"
      ]
    }
  ];

  const merchantServices = [
    {
      icon: CreditCard,
      title: "Payment Processing",
      description: "Accept all major credit and debit cards",
      features: [
        "Competitive processing rates",
        "Next-day funding",
        "EMV chip card acceptance",
        "Contactless payments",
        "Mobile card readers",
        "24/7 customer support"
      ]
    },
    {
      icon: Store,
      title: "Point of Sale Systems",
      description: "Complete POS solutions for retail and restaurants",
      features: [
        "Cloud-based systems",
        "Inventory management",
        "Employee management",
        "Sales reporting",
        "Customer management",
        "Integration capabilities"
      ]
    },
    {
      icon: Globe,
      title: "E-commerce Solutions",
      description: "Online payment gateway for your website",
      features: [
        "Secure online payments",
        "Shopping cart integration",
        "Fraud protection",
        "Recurring billing",
        "Mobile optimization",
        "API integration"
      ]
    }
  ];

  const cashManagement = [
    {
      icon: Zap,
      title: "ACH Processing",
      description: "Electronic payment processing for payroll and vendors"
    },
    {
      icon: Shield,
      title: "Positive Pay",
      description: "Fraud protection for checks and ACH transactions"
    },
    {
      icon: FileText,
      title: "Remote Deposit Capture",
      description: "Deposit checks from your office with digital scanning"
    },
    {
      icon: Calculator,
      title: "Account Reconciliation",
      description: "Automated matching of transactions and statements"
    },
    {
      icon: Globe,
      title: "Wire Transfers",
      description: "Domestic and international wire transfer services"
    },
    {
      icon: BarChart3,
      title: "Reporting & Analytics",
      description: "Detailed financial reporting and cash flow analysis"
    }
  ];

  const businessTypes = [
    {
      icon: Store,
      title: "Retail Businesses",
      description: "Complete banking solutions for retail operations",
      services: ["POS Systems", "Merchant Services", "Business Checking", "Equipment Loans"]
    },
    {
      icon: Briefcase,
      title: "Professional Services",
      description: "Banking for consultants, lawyers, and service providers",
      services: ["Business Checking", "Lines of Credit", "Payroll Services", "Cash Management"]
    },
    {
      icon: Factory,
      title: "Manufacturing",
      description: "Specialized solutions for manufacturing companies",
      services: ["Equipment Financing", "Commercial Real Estate", "Cash Management", "SBA Loans"]
    },
    {
      icon: Truck,
      title: "Transportation",
      description: "Banking solutions for logistics and transportation",
      services: ["Fleet Financing", "Fuel Cards", "Cash Management", "Equipment Loans"]
    }
  ];

  const benefits = [
    {
      icon: Users,
      title: "Dedicated Relationship Manager",
      description: "Personal banker who understands your business and industry"
    },
    {
      icon: Clock,
      title: "Quick Decision Making",
      description: "Local decision makers for faster loan approvals and service"
    },
    {
      icon: Handshake,
      title: "Community Partnership",
      description: "We invest in your success and our local community"
    },
    {
      icon: Shield,
      title: "Advanced Security",
      description: "Enterprise-level security to protect your business data"
    },
    {
      icon: Lightbulb,
      title: "Business Advisory",
      description: "Expert financial guidance to help grow your business"
    },
    {
      icon: Award,
      title: "Award-Winning Service",
      description: "Recognized excellence in commercial banking services"
    }
  ];

  const testimonials = [
    {
      name: "Michael Rodriguez",
      company: "Rodriguez Construction",
      role: "CEO",
              content: "First Capital International helped us secure equipment financing that allowed us to expand our operations. Their business banking team truly understands our industry.",
      rating: 5
    },
    {
      name: "Sarah Chen",
      company: "TechStart Solutions",
      role: "Founder",
      content: "The SBA loan process was seamless with their expert guidance. We couldn't have launched our startup without their support.",
      rating: 5
    },
    {
      name: "David Thompson",
      company: "Thompson's Market",
      role: "Owner",
      content: "Their merchant services and POS system completely transformed how we handle customer payments. Sales have increased 30%.",
      rating: 5
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/90 to-primary-800/90"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
              Business Banking
              <span className="block text-3xl md:text-5xl bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
                That Powers Growth
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 leading-relaxed max-w-3xl mx-auto mb-8">
                              Partner with First Capital International for comprehensive business banking solutions. 
              From startups to established enterprises, we provide the financial tools and expertise your business needs to thrive.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-primary-900 bg-white rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <Building2 className="w-6 h-6 mr-2" />
                Open account
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-xl hover:bg-white hover:text-primary-900 transition-all duration-300"
              >
                <Phone className="w-6 h-6 mr-2" />
                Schedule Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Business Account Types Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Business Account Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Choose from our comprehensive range of business accounts designed to meet the unique needs of your company, from sole proprietorships to large corporations.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {businessSolutions.map((account, index) => (
              <motion.div
                key={account.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative group"
              >
                {account.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 h-full border-2 border-transparent hover:border-primary-200">
                  <div className={`w-16 h-16 bg-gradient-to-r ${account.bgColor} rounded-xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300`}>
                    <account.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{account.title}</h3>
                  <p className="text-gray-600 mb-4">{account.description}</p>
                  
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-500">Starting Balance:</span>
                      <span className="font-semibold text-gray-900">{account.startingBalance}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Monthly Fee:</span>
                      <span className="font-semibold text-gray-900">{account.monthlyFee}</span>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {account.features.slice(0, 6).map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/register"
                    className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 px-4 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-300 font-semibold text-center block"
                  >
                    Open account
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Lending Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Business Lending Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Fuel your business growth with our flexible lending options. From equipment financing to commercial real estate, we have the capital solutions you need.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {lendingSolutions.map((loan, index) => (
              <motion.div
                key={loan.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-2xl p-8 border border-primary-200 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{loan.title}</h3>
                    <p className="text-gray-600">{loan.description}</p>
                  </div>
                  <loan.icon className="w-12 h-12 text-primary-600" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div>
                    <span className="text-sm text-gray-500">Starting Rate</span>
                    <div className="font-semibold text-gray-900">{loan.rates}</div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Max Amount</span>
                    <div className="font-semibold text-gray-900">{loan.maxAmount}</div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Terms</span>
                    <div className="font-semibold text-gray-900">{loan.terms}</div>
                  </div>
                </div>

                <ul className="space-y-2 mb-6">
                  {loan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/loans"
                  className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors duration-300 font-semibold text-center block"
                >
                  Apply Now
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Merchant Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Merchant Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Accept payments anywhere, anytime with our comprehensive merchant solutions. Increase sales and improve customer experience with modern payment processing.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {merchantServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <service.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{service.title}</h3>
                <p className="text-gray-600 mb-6 text-center">{service.description}</p>
                
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cash Management Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Cash Management Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Streamline your business finances with our advanced cash management tools. Automate processes, reduce costs, and gain better control over your cash flow.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cashManagement.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <service.icon className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Types Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Industry-Specific Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We understand that different industries have unique banking needs. Our specialized solutions are tailored to support businesses across various sectors.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {businessTypes.map((business, index) => (
              <motion.div
                key={business.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <business.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{business.title}</h3>
                <p className="text-gray-600 mb-4">{business.description}</p>
                
                <div className="space-y-1">
                  {business.services.map((service, idx) => (
                    <div key={idx} className="text-sm text-primary-600 font-medium">
                      {service}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Choose Our Business Banking?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Experience the benefits of partnering with a community bank that's invested in your success. We combine personal service with modern technology.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                              Hear from business owners who've partnered with First Capital International to achieve their goals and grow their companies.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <blockquote className="text-gray-600 mb-6 italic">
                  "{testimonial.content}"
                </blockquote>
                <div>
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-primary-600 font-medium">{testimonial.role}</div>
                  <div className="text-gray-500 text-sm">{testimonial.company}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Grow Your Business?
            </h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                              Let First Capital International be your trusted financial partner. Contact our business banking team today to discuss customized solutions for your company.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-primary-600 bg-white rounded-xl hover:bg-gray-50 transition-colors duration-300 shadow-lg hover:shadow-xl"
              >
                <Building2 className="w-6 h-6 mr-2" />
                Open account
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-xl hover:bg-white hover:text-primary-600 transition-colors duration-300"
              >
                <Users className="w-6 h-6 mr-2" />
                Schedule Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BusinessBanking; 